<template>
  <div class="railway-calculator">
    <div class="calculator-header">
      <h3>
        <i class="bx bx-train"></i>
        Калькулятор железнодорожных перевозок
      </h3>
    </div>
    
    <div class="calculator-content">
      <!-- Переключатель FCL/LCL -->
      <div class="transport-type-selector">
        <div class="selector-tabs">
          <button 
            :class="['tab-button', { active: transportType === 'fcl' }]"
            @click="transportType = 'fcl'"
          >
            <i class="bx bx-package"></i>
            FCL (Контейнер)
          </button>
          <button 
            :class="['tab-button', { active: transportType === 'lcl' }]"
            @click="transportType = 'lcl'"
          >
            <i class="bx bx-cube"></i>
            LCL (Сборный груз)
          </button>
        </div>
      </div>

      <!-- Форма FCL -->
      <div v-if="transportType === 'fcl'" class="calculator-form">
        <div class="form-row three-columns">
          <div class="form-group">
            <label>Город отправления</label>
            <select v-model="fclForm.originCity" @change="calculateFCL">
              <option value="">Выберите город</option>
              <option 
                v-for="city in uniqueCities" 
                :key="city"
                :value="city"
              >
                {{ city }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>POD (Порт назначения)</label>
            <select v-model="fclForm.pod" @change="calculateFCL">
              <option value="">Выберите POD</option>
              <option 
                v-for="pod in uniquePods" 
                :key="pod"
                :value="pod"
              >
                {{ pod }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>Количество контейнеров</label>
            <input 
              type="number" 
              v-model="fclForm.containerCount" 
              placeholder="Введите количество"
              @input="calculateFCL"
              min="1"
            >
          </div>
        </div>
        
        <!-- <div class="form-row">
          <div class="form-group">
            <label>Тип контейнера</label>
            <select v-model="fclForm.containerType" @change="calculateFCL">
              <option value="40hq">40HQ</option>
            </select>
          </div>
        </div> -->
      </div>

      <!-- Форма LCL -->
      <div v-if="transportType === 'lcl'" class="calculator-form">
        <div class="form-row">
          <div class="form-group">
            <label>Вес, кг</label>
            <input 
              type="number" 
              v-model="lclForm.weight" 
              placeholder="Введите вес"
              @input="calculateLCL"
            >
          </div>
          <div class="form-group">
            <label>Объем, м³</label>
            <input 
              type="number" 
              v-model="lclForm.volume" 
              placeholder="Введите объем"
              @input="calculateLCL"
            >
          </div>
        </div>
      </div>
      
      <!-- Результаты расчета FCL -->
      <div class="calculation-results" v-if="transportType === 'fcl' && fclResult">
        <div class="results-header">
          <h4>Результат расчета FCL</h4>
        </div>
        
        <div class="results-content">
          <div class="result-item">
            <span class="label">Маршрут:</span>
            <span class="value">{{ fclResult.originCity }} → {{ fclResult.pod }}</span>
          </div>
          <div class="result-item">
            <span class="label">Количество контейнеров:</span>
            <span class="value">{{ fclResult.containerCount }} шт.</span>
          </div>
          <div class="result-item">
            <span class="label">Стоимость за контейнер:</span>
            <span class="value">${{ fclResult.costPerContainer.toFixed(2) }}</span>
          </div>
          <div class="result-item total">
            <span class="label">Общая стоимость:</span>
            <span class="value">${{ fclResult.totalCost.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Результаты расчета LCL -->
      <div class="calculation-results" v-if="transportType === 'lcl' && lclResult">
        <div class="results-header">
          <h4>Результат расчета LCL</h4>
        </div>
        
        <div class="results-content">
          <div class="result-item">
            <span class="label">Вес:</span>
            <span class="value">{{ lclForm.weight }} кг</span>
          </div>
          <div class="result-item">
            <span class="label">Объем:</span>
            <span class="value">{{ lclForm.volume }} м³</span>
          </div>
          <div class="result-item">
            <span class="label">Расчетный вес:</span>
            <span class="value">{{ lclResult.calculatedWeight }} кг</span>
          </div>
          <div class="result-item">
            <span class="label">Express стоимость:</span>
            <span class="value">${{ lclResult.expressCost.toFixed(2) }}</span>
          </div>
          <div class="result-item">
            <span class="label">Handling charges:</span>
            <span class="value">${{ lclResult.handlingCharges.toFixed(2) }}</span>
          </div>
          <div class="result-item total">
            <span class="label">Общая ставка:</span>
            <span class="value">${{ lclResult.totalRate.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Информационные карточки FCL и LCL -->
      <div class="info-cards">
        <h4>Что такое FCL и LCL?</h4>
        <div class="info-cards-grid">
          <div class="info-card fcl-card">
            <div class="card-header">
              <div class="card-icon">
                <i class="bx bx-package"></i>
              </div>
              <h5>FCL (Full Container Load)</h5>
              <span class="card-subtitle">Полный контейнер</span>
            </div>
            <div class="card-content">
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Вы арендуете весь контейнер</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Подходит для больших объемов груза</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Более выгодно при объеме от 15-20 м³</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Быстрая погрузка/выгрузка</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Меньше рисков повреждения груза</span>
              </div>
            </div>
            <div class="card-footer">
              <div class="price-info">
                <span class="price-label">Стоимость:</span>
                <span class="price-value">Фиксированная за контейнер</span>
              </div>
            </div>
          </div>

          <div class="info-card fcl-card">
            <div class="card-header">
              <div class="card-icon">
                <i class="bx bx-cube"></i>
              </div>
              <h5>LCL (Less than Container Load)</h5>
              <span class="card-subtitle">Сборный груз</span>
            </div>
            <div class="card-content">
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Ваш груз делит контейнер с другими</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Подходит для малых объемов</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Экономично при объеме до 15 м³</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Гибкость в планировании</span>
              </div>
              <div class="info-item">
                <i class="bx bx-check-circle"></i>
                <span>Возможность отправки небольших партий</span>
              </div>
            </div>
            <div class="card-footer">
              <div class="price-info">
                <span class="price-label">Стоимость:</span>
                <span class="price-value">За объем или вес (что больше)</span>
              </div>
            </div>
          </div>
        </div>

        <div class="comparison-table">
          <h5>Сравнение FCL и LCL в железнодорожных перевозках</h5>
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Критерий</th>
                  <th>FCL</th>
                  <th>LCL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Объем груза</strong></td>
                  <td>15+ м³</td>
                  <td>До 15 м³</td>
                </tr>
                <tr>
                  <td><strong>Стоимость</strong></td>
                  <td>Фиксированная за контейнер</td>
                  <td>За объем/вес</td>
                </tr>
                <tr>
                  <td><strong>Срок доставки</strong></td>
                  <td>Быстрее</td>
                  <td>Медленнее (консолидация)</td>
                </tr>
                <tr>
                  <td><strong>Риски</strong></td>
                  <td>Минимальные</td>
                  <td>Средние</td>
                </tr>
                <tr>
                  <td><strong>Контроль</strong></td>
                  <td>Полный</td>
                  <td>Ограниченный</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_CALCULATOR_SETTINGS_RAILWAY } from '@/constants'

export default {
  name: 'RailwayCalculator',
  
  data() {
    return {
      isLoading: false,
      transportType: 'fcl', // fcl или lcl
      fclTariffs: [],
      lclSettings: {},
      fclForm: {
        originCity: '',
        pod: '',
        containerCount: 1,
        containerType: '40hq'
      },
      lclForm: {
        weight: '',
        volume: ''
      },
      fclResult: null,
      lclResult: null
    }
  },
  
  computed: {
    uniqueCities() {
      const cities = [...new Set(this.fclTariffs.map(tariff => tariff.origin_city))]
      return cities.sort()
    },
    
    uniquePods() {
      const pods = [...new Set(this.fclTariffs.map(tariff => tariff.pod))]
      return pods.sort()
    }
  },
  
  async mounted() {
    await this.loadSettings()
  },
  
  methods: {
    async loadSettings() {
      try {
        this.isLoading = true
        const response = await axios.get(API_CALCULATOR_SETTINGS_RAILWAY)
        
        if (response.data.success && response.data.data) {
          this.fclTariffs = response.data.data.fcl_tariffs || []
          this.lclSettings = response.data.data.lcl_settings || {}
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error)
        this.$toast.error('Ошибка загрузки настроек калькулятора')
      } finally {
        this.isLoading = false
      }
    },
    
    calculateFCL() {
      if (!this.fclForm.originCity || !this.fclForm.pod || !this.fclForm.containerCount) {
        this.fclResult = null
        return
      }
      
      // Находим подходящий тариф
      const tariff = this.fclTariffs.find(t => 
        t.origin_city === this.fclForm.originCity && 
        t.pod === this.fclForm.pod
      )
      
      if (!tariff) {
        this.fclResult = null
        return
      }
      
      const containerCount = parseInt(this.fclForm.containerCount)
      const costPerContainer = tariff.cost_40hq // Используем стоимость 40HQ
      const totalCost = containerCount * costPerContainer
      
      this.fclResult = {
        originCity: this.fclForm.originCity,
        pod: this.fclForm.pod,
        containerCount: containerCount,
        costPerContainer: costPerContainer,
        totalCost: totalCost
      }
    },
    
    calculateLCL() {
      if (!this.lclForm.weight || !this.lclForm.volume) {
        this.lclResult = null
        return
      }
      
      const weight = parseFloat(this.lclForm.weight)
      const volume = parseFloat(this.lclForm.volume)
      
      // Формула из Excel: =ЕСЛИ(B22<B23*500;B19*B23+B20;B22/500*B19+B20)
      // B22 - вес, B23 - объем, B19 - express_cost, B20 - handling_charges
      // Если вес < объем*500, то express_cost*объем + handling_charges
      // Иначе вес/500*express_cost + handling_charges
      
      const expressCost = this.lclSettings.express_cost || 120
      const handlingCharges = this.lclSettings.handling_charges || 200
      
      let calculatedWeight
      let totalRate
      
      if (weight < volume * 500) {
        // Если вес меньше объем*500, используем объем
        calculatedWeight = volume * 500
        totalRate = expressCost * volume + handlingCharges
      } else {
        // Иначе используем вес
        calculatedWeight = weight
        totalRate = (weight / 500) * expressCost + handlingCharges
      }
      
      this.lclResult = {
        weight: weight,
        volume: volume,
        calculatedWeight: calculatedWeight,
        expressCost: expressCost,
        handlingCharges: handlingCharges,
        totalRate: totalRate
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.railway-calculator {
  .calculator-header {
    margin-bottom: 20px;
    
    h3 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #0d6348;
      font-size: 18px;
      
      i {
        font-size: 20px;
      }
    }
  }
  
  .calculator-content {
    .transport-type-selector {
      margin-bottom: 20px;
      
      .selector-tabs {
        display: flex;
        background: #fff;
        border-radius: 6px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        overflow: hidden;
        
        .tab-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fff;
          border: none;
          border-right: 1px solid #e9ecef;
          color: #666;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          
          &:last-child {
            border-right: none;
          }
          
          i {
            font-size: 16px;
          }
          
          &:hover {
            background: #f8f9fa;
            color: #0d6348;
          }
          
          &.active {
            background: #0d6348;
            color: white;
            
            &:hover {
              background: #0a4d3a;
            }
          }
        }
      }
    }
    
    .calculator-form {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      
      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
        margin-bottom: 15px;
        
        &.three-columns {
          grid-template-columns: 1fr 1fr 1fr;
          
          @media (max-width: 1200px) {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          
          &.three-columns {
            grid-template-columns: 1fr;
          }
        }
      }
      
      .form-group {
        display: flex;
        flex-direction: column;
        
        label {
          font-size: 12px;
          font-weight: 500;
          color: #333;
          margin-bottom: 5px;
        }
        
        input, select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.3s;
          
          &:focus {
            outline: none;
            border-color: #0d6348;
          }
        }
      }
    }
    
    .calculation-results {
      background: #f8f9fa;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
      border: 1px solid #e9ecef;
      
      .results-header {
        margin-bottom: 15px;
        
        h4 {
          margin: 0;
          color: #0d6348;
          font-size: 16px;
        }
      }
      
      .results-content {
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
          
          &.total {
            border-bottom: none;
            font-weight: 600;
            font-size: 16px;
            color: #0d6348;
            margin-top: 10px;
            padding-top: 15px;
            border-top: 2px solid #0d6348;
          }
          
          .label {
            color: #666;
            font-size: 14px;
          }
          
          .value {
            font-weight: 500;
            color: #333;
          }
        }
      }
    }
    
    .info-cards {
      margin-top: 30px;
      
      h4 {
        margin-bottom: 20px;
        color: #0d6348;
        font-size: 18px;
        font-weight: 600;
      }
      
      .info-cards-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-bottom: 30px;
        
        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }
      }
      
      .info-card {
        background: #fff;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border: 1px solid #e9ecef;
        transition: all 0.3s ease;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
        
        &.fcl-card {
          border-left: 4px solid #0d6348;
          
          .card-icon {
            background: linear-gradient(135deg, #0d6348, #1a7a5e);
          }
        }
        
        &.lcl-card {
          border-left: 4px solid #6c757d;
          
          .card-icon {
            background: linear-gradient(135deg, #6c757d, #868e96);
          }
        }
        
        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          
          .card-icon {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            
            i {
              font-size: 24px;
            }
          }
          
          h5 {
            margin: 0;
            color: #0d6348;
            font-size: 16px;
            font-weight: 600;
          }
          
          .card-subtitle {
            color: #666;
            font-size: 12px;
            font-weight: 500;
          }
        }
        
        .card-content {
          margin-bottom: 16px;
          
          .info-item {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
            font-size: 14px;
            color: #333;
            
            i {
              color: #28a745;
              font-size: 16px;
            }
            
            span {
              line-height: 1.4;
            }
          }
        }
        
        .card-footer {
          border-top: 1px solid #e9ecef;
          padding-top: 12px;
          
          .price-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
            
            .price-label {
              font-size: 12px;
              color: #666;
              font-weight: 500;
            }
            
            .price-value {
              font-size: 14px;
              color: #0d6348;
              font-weight: 600;
            }
          }
        }
      }
      
      .comparison-table {
        background: #fff;
        border-radius: 12px;
        padding: 20px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        border: 1px solid #e9ecef;
        
        h5 {
          margin: 0 0 16px 0;
          color: #0d6348;
          font-size: 16px;
          font-weight: 600;
        }
        
        .table-container {
          overflow-x: auto;
          
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;
            
            thead {
              background: #f8f9fa;
              
              th {
                padding: 12px 16px;
                text-align: left;
                font-weight: 600;
                color: #0d6348;
                border-bottom: 2px solid #0d6348;
              }
            }
            
            tbody {
              tr {
                border-bottom: 1px solid #e9ecef;
                
                &:hover {
                  background: #f8f9fa;
                }
                
                td {
                  padding: 12px 16px;
                  color: #333;
                  
                  strong {
                    color: #0d6348;
                    font-weight: 600;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>